import { Input } from '@ntconsult/aluma-react';
import { Check, Search, X } from 'lucide-react';
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { PaginatedListResponse } from '../types/Filters';

type SearchResult<T> = {
  data?: PaginatedListResponse<T>;
  isFetching: boolean;
};

type MultiSearchOption = { id: number; name: string };

export type MultiSearchComboboxProps<T extends { id: number }, V extends MultiSearchOption> = {
  /** O `<label htmlFor>` do `FormField` não nomeia uma `<div>`, só elementos de formulário. */
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  removeChipLabel: (name: string) => string;
  value: V[];
  onChange: (next: V[]) => void;
  useSearch: (text: string) => SearchResult<T>;
  toOption: (item: T) => V;
  /** Texto da opção na lista, quando difere do nome que vai para o chip. */
  getItemLabel?: (item: T) => string;
  renderMarker?: (option: V) => ReactNode;
  disabled?: boolean;
  /** Injetados pelo `FormField` via `cloneElement`. */
  id?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean | 'true' | 'false';
};

const PANEL_GAP = 4;
const PANEL_MARGIN = 8;

type PanelPosition = {
  placement: 'above' | 'below';
  y: number;
  left: number;
  width: number;
  maxHeight: number;
};

/**
 * Composto ad-hoc: o `Combobox` do Aluma (`multiple`) só filtra `Option`s já
 * montadas em memória, sem busca no servidor.
 *
 * O painel é `fixed` (não `absolute`) para não ser cortado pelo
 * `overflow-hidden` do `AllocationModal`, e vai por portal para o `<dialog>`
 * ancestral: no `document.body` ficaria atrás do top layer do modal/drawer.
 */
export function MultiSearchCombobox<T extends { id: number }, V extends MultiSearchOption>({
  label,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  removeChipLabel,
  value,
  onChange,
  useSearch,
  toOption,
  getItemLabel,
  renderMarker,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
}: MultiSearchComboboxProps<T, V>) {
  const { t } = useTranslation('alocacao');
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const { data, isFetching } = useSearch(text);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const selectedIds = new Set(value.map((option) => option.id));

  useLayoutEffect(() => {
    if (!open) return;
    setPortalContainer(triggerRef.current?.closest('dialog') ?? document.body);

    function reposition() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom - PANEL_MARGIN;
      const spaceAbove = rect.top - PANEL_MARGIN;
      const placement = spaceBelow < 160 && spaceAbove > spaceBelow ? 'above' : 'below';
      setPosition({
        placement,
        left: rect.left,
        width: rect.width,
        y: placement === 'above' ? rect.top - PANEL_GAP : rect.bottom + PANEL_GAP,
        maxHeight: Math.max(120, placement === 'above' ? spaceAbove : spaceBelow),
      });
    }

    reposition();
    window.addEventListener('resize', reposition);
    document.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      document.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  function toggle(item: T) {
    onChange(
      selectedIds.has(item.id)
        ? value.filter((option) => option.id !== item.id)
        : [...value, toOption(item)],
    );
  }

  function removeChip(optionId: number) {
    onChange(value.filter((option) => option.id !== optionId));
  }

  return (
    <div
      ref={triggerRef}
      id={id}
      aria-label={label}
      aria-describedby={ariaDescribedBy}
      aria-required={ariaRequired}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={listboxId}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && setOpen(true)}
      onKeyDown={(e) => {
        // Teclas da busca (portal) e dos chips sobem até aqui pela árvore do React.
        if (disabled || e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
        }
      }}
      className={`flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 text-sm cursor-pointer transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2${
        disabled ? ' pointer-events-none cursor-not-allowed opacity-50' : ''
      }`}
    >
      {value.length === 0 && <span className="px-1 text-foreground-subtle">{placeholder}</span>}
      {value.map((option) => (
        <span
          key={option.id}
          className="flex items-center gap-1 rounded-full bg-muted py-0.5 pl-2 pr-1 text-xs text-foreground"
        >
          {renderMarker?.(option)}
          {option.name}
          <button
            type="button"
            aria-label={removeChipLabel(option.name)}
            className="flex items-center rounded-full text-foreground-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            onClick={(e) => {
              e.stopPropagation();
              removeChip(option.id);
            }}
          >
            <X size={12} />
          </button>
        </span>
      ))}

      {open &&
        !disabled &&
        position !== null &&
        portalContainer !== null &&
        createPortal(
          <div
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            style={{
              position: 'fixed',
              top: position.placement === 'below' ? position.y : undefined,
              bottom: position.placement === 'above' ? window.innerHeight - position.y : undefined,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
            className="z-50 flex flex-col rounded-lg border border-border bg-surface p-1 shadow-lg"
          >
            <div className="p-1">
              <Input
                autoFocus
                size="sm"
                aria-label={searchPlaceholder}
                leftIcon={<Search size={14} />}
                placeholder={searchPlaceholder}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={() => setOpen(false)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return;
                  // Sem isso o Esc vira o `cancel` do `<dialog>` e fecha o modal/drawer junto.
                  e.preventDefault();
                  setOpen(false);
                }}
              />
            </div>
            <div className="overflow-y-auto overflow-x-hidden">
              {isFetching && (
                <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
                  {t('filtros.buscando')}
                </div>
              )}
              {!isFetching && items.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
                  {emptyLabel}
                </div>
              )}
              {!isFetching &&
                items.map((item) => {
                  const option = toOption(item);
                  const selected = selectedIds.has(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggle(item)}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-brand-primary">
                        {selected && <Check size={14} />}
                      </span>
                      {renderMarker?.(option)}
                      {getItemLabel?.(item) ?? option.name}
                    </button>
                  );
                })}
            </div>
            {!isFetching && total > items.length && (
              <div className="border-t border-border px-3 pt-2 pb-1 text-xs text-foreground-subtle">
                {t('filtros.mostrandoDeTotal', { shown: items.length, total })}
              </div>
            )}
          </div>,
          portalContainer,
        )}
    </div>
  );
}
